import { handleApiError } from './errorHandler';

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  ok: boolean;
}

export const handleFetchResponse = async <T = any>(response: Response): Promise<T> => {
  // Check if the response is ok (status 200-299)
  if (!response.ok) {
    // Create an error object that mimics axios error structure
    const error = {
      response: {
        status: response.status,
        data: await response.json().catch(() => ({ error: 'Failed to parse error response' }))
      }
    };
    const apiError = handleApiError(error);
    throw new Error(apiError.message);
  }

  // Parse the response JSON
  return await response.json();
};

export const createApiRequest = async <T = any>(
  url: string, 
  options: RequestInit = {}
): Promise<T> => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    return await handleFetchResponse<T>(response);
  } catch (error) {
    // Re-throw the error with proper message
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

export const createAuthenticatedRequest = async <T = any>(
  url: string, 
  options: RequestInit = {}
): Promise<T> => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }

  return createApiRequest<T>(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
}; 